import angular from "angular";

import {
  Calculation,
  CalculationRowType,
  Dates,
  PeriodDateKind,
  PeriodType,
  Reports,
  SummaryReportData,
  Avatar,
  Translations,
  Numeric,
  Employments,
  EnumerationsLogic,
  EmploymentTerminationKind,
  IrRow,
  IncomeTypesLogic,
  WorkingTimeCompensationKind,
  RemunerationKind,
  OtherCompensationKind,
  NonProfitOrgKind,
  TrainingKind,
  Unit,
  PaidSickLeaveKind,
  DailyAllowanceKind,
  AllowanceCode,
  Objects,
  Arrays
} from "@salaxy/core";
import { SummaryMapper, UnionPaymentReportConfigGenerator, CostCenterReportConfigGenerator } from "@salaxy/reports";
import { UiHelpers, ExcelHelpers, SessionService, UiCrudHelpers } from "../../services";
import { SxyTableController } from "../sxy-table/SxyTableController";

/**
 * Component for displaying summary report of selected period.
 */
export class SummaryReportQueryController implements angular.IController {
  /** Parameters related to report query*/
  public queryOptions: {
    /** Period type for the query. */
    periodType: PeriodType,
    /** Ref date for the period. */
    refDate?: string,
    /** End date for the custom period. */
    endDate?: string,
    /** Period date kind */
    periodDateKind: PeriodDateKind,
  } = {
    periodType: PeriodType.Year,
    refDate: Dates.getDate(Dates.getYear("today"), 1, 1),
    endDate: Dates.getDate(Dates.getYear("today"), 12, 31),
    periodDateKind: PeriodDateKind.SalaryDate,
  };

  /** Result rows */
  public resultRows: ResultRow[] = [];

  /** Current tab. */
  public currentTab: "incomereport" | "salarypaymentreport" = "incomereport";

  public irReportRows: IrCodeIncomeReportRow[] = [];
  public entrepreneurSummaryReportResults:SummaryReportRowResult[] = [];
  public workerSummaryReportResults: SummaryReportRowResult[] = [];
  public totalSummaryReportResults:SummaryReportRowResult[] = [];
  private summaryReportData: SummaryReportData;
  public reportLang: string;
  public costCenterReportCalculations: any = [];
  public unionPaymentExcelRows: any[] = [];
  public reportConfig: any = {};

  /** Ref to used SxyTable */
  public $sxyTable: SxyTableController;

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Reports",
    "UiHelpers",
    "Employments",
    "UiCrudHelpers",
    "SessionService",
    "$timeout"
  ];

  constructor(private reports: Reports, private uiHelpers: UiHelpers, private employments: Employments, private uiCrudHelpers: UiCrudHelpers, private sessionService: SessionService, private $timeout: angular.ITimeoutService) {
  }

  /** Controller initialization */
  public $onInit() {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    this.sessionService.getReportLang().then(lang => {
      this.reportLang = lang;
    });
    this.query().then( () => {
      loading.dismiss();
    });
  }

  /** Return query info text */
  public getQueryInfoText(): string {
    if (this.summaryReportData?.startDate && this.summaryReportData?.endDate) {
      return `${Translations.get("SALAXY.NG1.HolidayReportComponent.irIncomeTypeReportInfo")}: ${Dates.getFormattedDate(this.summaryReportData.startDate)} - ${Dates.getFormattedDate(this.summaryReportData.endDate)}`;
    }
    return "";
  }

  /**
   * Shows the period selection dialog and makes the search.
   */
  public showPeriodSelection() {
    this.uiHelpers.openEditDialog(
      "salaxy-components/report/modals/SummaryReportPeriodSelection.html",
      this.queryOptions,
      null).then((periodResult) => {
        if (periodResult.action === "ok") {
          this.queryOptions = periodResult.item;
          this.query();
        }
      });
  }

  /**
   * Shows an edit dialog for the employment.
   * @param row Row that contains field employmentId (Identifier for the employment).
   * @param row.employmentId The row must contain the employment identifier.
   */
  public showEditEmploymentDialog(row: { employmentId: string } ): void {
    if (!row.employmentId) {
      console.error("EmploymentId is missing", row);
      return;
    }
    this.uiCrudHelpers.openEditEmploymentDialog(row.employmentId, "default").then((result) => {
      if (result.action === "ok") {
        this.query();
      }
    });
  }

  /**
   * Filters zero rows off
   * @returns IrCodeIncomeReportRow[] containing not zero rows
   */
  public getIrReportRows(): IrCodeIncomeReportRow[] {
    const results: IrCodeIncomeReportRow[] = [];
    const irRows = this.resultRows.map(x => x.irCodeIncomeReportRow);
    irRows.forEach( irRow => {
      if(irRow) {
        results.push(irRow);
      }
    });
    return results;
  }

  /**
   * Groups each result rows summary report entrepreneur results
   * @returns array of grouped entrepreneur results
   */
  public getEntrepreneurSummaryReportResults(): SummaryReportRowResult[] {
    const results = [];
    this.resultRows.forEach(resultRow => {
      if(resultRow?.summaryReportRow?.result?.entrepreneurResult) {
        resultRow.summaryReportRow.result = resultRow.summaryReportRow.result || {};
        resultRow.summaryReportRow.result.entrepreneurResult = resultRow.summaryReportRow.result.entrepreneurResult || this.getBlankSummaryReportRowResult();
        resultRow.summaryReportRow.result.entrepreneurResult.avatar = resultRow.avatar;
        resultRow.summaryReportRow.result.entrepreneurResult.workerId = resultRow.workerId;
        resultRow.summaryReportRow.result.entrepreneurResult.employmentId = resultRow.employmentId;
        results.push(resultRow.summaryReportRow.result.entrepreneurResult);
      }
    });
    return results;
  }

  /**
   * Groups each result rows summary report worker results
   * @returns array of grouped worker results
   */
  public getWorkerSummaryReportResults(): SummaryReportRowResult[] {
    const results = [];
    this.resultRows.forEach(resultRow => {
      if(resultRow.summaryReportRow.result.workerResult) {
        resultRow.summaryReportRow.result.workerResult.avatar = resultRow.avatar;
        resultRow.summaryReportRow.result.workerResult.workerId = resultRow.workerId;
        resultRow.summaryReportRow.result.workerResult.employmentId = resultRow.employmentId;
        results.push(resultRow.summaryReportRow.result.workerResult);
      }
    });
    return results;
  }

  /**
   * Export totalRows to excel
   */
   public exportToExcel() {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    let tableData = [];
    let reportName = "";
    if (this.currentTab == "incomereport") {
      reportName = "incomeReport";
      const irReportRows = this.irReportRows;
      if (irReportRows.length > 0) {
        tableData.push([Translations.get("SALAXY.NG1_Other.Roles.worker"), ...irReportRows[0].rows.map(row => row.label)]);
        const rowData = irReportRows.map(row => {
          return [row.workerAvatar.displayName, ...row.rows.map(item => item.total)];
        });
        tableData.push(...rowData);
      }
    } else if (this.currentTab == "salarypaymentreport") {
      reportName = "salaryPaymentReport";
      const headers = [
        Translations.get("SALAXY.NG1_Other.Roles.worker"),
        Translations.get("SALAXY.REPORTS.summary.table.totalGrossSalary"),
        Translations.get("SALAXY.REPORTS.summary.table.totalRowCategory"),
        Translations.get("SALAXY.REPORTS.summary.table.benefits"),
        Translations.get("SALAXY.REPORTS.summary.table.irCode"),
        Translations.get("SALAXY.REPORTS.summary.table.totalTaxable"),
        Translations.get("SALAXY.REPORTS.summary.table.tax"),
        Translations.get("SALAXY.REPORTS.summary.table.pension"),
        Translations.get("SALAXY.REPORTS.summary.table.unemploymentInsurance"),
        Translations.get("SALAXY.REPORTS.summary.table.unionPayment"),
        Translations.get("SALAXY.REPORTS.summary.table.foreclosure"),
        Translations.get("SALAXY.REPORTS.summary.table.otherDeductions"),
        Translations.get("SALAXY.REPORTS.summary.table.totalExpenses"),
        Translations.get("SALAXY.REPORTS.summary.table.totalWorkerPayment"),
        Translations.get("SALAXY.REPORTS.summary.table.totalPayment"),
        Translations.get("SALAXY.REPORTS.summary.table.palkkaus"),
        Translations.get("SALAXY.REPORTS.summary.table.service"),
        Translations.get("SALAXY.REPORTS.summary.table.socialSecurity"),
        Translations.get("SALAXY.REPORTS.summary.table.totalPensions"),
        Translations.get("SALAXY.REPORTS.summary.table.employerPension"),
        Translations.get("SALAXY.REPORTS.summary.table.unemployment"),
        Translations.get("SALAXY.REPORTS.summary.table.employerUnemployment")
      ];
      tableData.push(headers);

      if (this.entrepreneurSummaryReportResults.length > 0) {
        const rowData = this.entrepreneurSummaryReportResults.map(row => {
          return [
            `${row.avatar.displayName} (YEL)`,
            row.totalGrossSalary,
            row.totalRowCategory,
            row.benefits,
            row.irCode,
            row.totalTaxable,
            row.tax,
            row.pension,
            row.unemploymentInsurance,
            row.unionPayment,
            row.foreclosure,
            row.otherDeductions,
            row.totalExpenses,
            row.totalWorkerPayment,
            row.totalPayment,
            row.palkkaus,
            row.service,
            row.socialSecurity,
            row.totalPensions,
            row.employerPension,
            row.unemployment,
            row.employerUnemployment,
          ];
        });
        tableData.push(...rowData);
      }
      if (this.workerSummaryReportResults.length > 0) {
        const rowData = this.workerSummaryReportResults.map(row => {
          return [
            `${row.avatar.displayName} (TyEL)`,
            row.totalGrossSalary,
            row.totalRowCategory,
            row.benefits,
            row.irCode,
            row.totalTaxable,
            row.tax,
            row.pension,
            row.unemploymentInsurance,
            row.unionPayment,
            row.foreclosure,
            row.otherDeductions,
            row.totalExpenses,
            row.totalWorkerPayment,
            row.totalPayment,
            row.palkkaus,
            row.service,
            row.socialSecurity,
            row.totalPensions,
            row.employerPension,
            row.unemployment,
            row.employerUnemployment,
          ];
        });
        tableData.push(...rowData);
      }
      if (this.workerSummaryReportResults.length > 0 || this.entrepreneurSummaryReportResults.length > 0) {
        const rowData = this.totalSummaryReportResults.map(row => {
          return [
            Translations.get("SALAXY.REPORTS.summary.table.total"),
            row.totalGrossSalary,
            row.totalRowCategory,
            row.benefits,
            row.irCode,
            row.totalTaxable,
            row.tax,
            row.pension,
            row.unemploymentInsurance,
            row.unionPayment,
            row.foreclosure,
            row.otherDeductions,
            row.totalExpenses,
            row.totalWorkerPayment,
            row.totalPayment,
            row.palkkaus,
            row.service,
            row.socialSecurity,
            row.totalPensions,
            row.employerPension,
            row.unemployment,
            row.employerUnemployment,
          ];
        });
        tableData.push(...rowData);
      }
    } else if (this.currentTab == "unionpaymentreport") {
      if (this.unionPaymentExcelRows.length > 0) {
        reportName = "unionPaymentReport";
        tableData = this.unionPaymentExcelRows;
      }
    } else if (this.currentTab == "costcenterreport") {
      if (this.reportConfig.costCenterReportTableConfig.data) {
        reportName ="costCenterReport";
        tableData = CostCenterReportConfigGenerator.createCostCenterExcelRows(this.reportConfig.costCenterReportTableConfig);
      }
    }

    reportName += "-" + Dates.getToday();
    if (tableData.length > 0) {
      ExcelHelpers.export(tableData, reportName).then(() => {
        loading.dismiss();
      });
    } else {
      loading.dismiss();
    }
  }

  private query(): Promise<SummaryReportData> {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    return this.reports.getSummaryReportData(`${Dates.asDate(this.queryOptions.refDate)}`, this.queryOptions.periodType, `${Dates.asDate(this.queryOptions.endDate)}`, this.queryOptions.periodDateKind).then(data => {
      this.summaryReportData = data;
      this.resultRows = [];

      const findAvatar = (employmentId: string) => {
        const filteredAndSorted = this.summaryReportData.calculations
          .filter(x => x.worker.employmentId === employmentId)
          .sort((a, b) => a.updatedAt > b.updatedAt ? -1 : 1);
        return filteredAndSorted[0]?.worker?.avatar;
      };

      this.summaryReportData?.calculations?.forEach(calc => {
        let resultRow = this.resultRows.find(x => x.employmentId === calc.worker?.employmentId);
        if(!resultRow) {
          const newRow = this.getBlankResultRow();
          const avatar = findAvatar(calc.worker.employmentId);
          newRow.employmentId = calc.worker.employmentId;
          newRow.avatar = avatar;
          newRow.workerId = avatar?.id;
          this.resultRows.push(newRow);
          resultRow = newRow;
        }
        resultRow.calculations.push(calc);
      });
      this.generateResults();
      loading.dismiss();
      return data;
    });
  }

  private generateResults(): void {
    this.createIrReportRows();
    this.createSummaryReportRows();
    this.irReportRows = this.getIrReportRows();
    this.entrepreneurSummaryReportResults = this.getEntrepreneurSummaryReportResults();
    this.workerSummaryReportResults = this.getWorkerSummaryReportResults();
    this.totalSummaryReportResults = this.resultRows[0]?.summaryReportRow?.result?.totalResult ? [this.resultRows[0].summaryReportRow.result.totalResult] : [];
    this.createUnionPaymentReportData();

    if (this.sessionService.isInRole("test")) {
      this.createCostCenterReportData();
    }
  }

  /**
   * Creates Union payment report data - for browser and excel export.
   */
  private async createUnionPaymentReportData() {
    this.unionPaymentExcelRows = [];
    const tableData: any[] = [];

    const headers = [
      { field: "worker", title: Translations.get("SALAXY.REPORTS.unionPaymentReport.worker") },
      { field: "salaryDate", title: Translations.get("SALAXY.REPORTS.unionPaymentReport.salaryDate") },
      { field: "salaryPeriodStart", title: Translations.get("SALAXY.REPORTS.unionPaymentReport.salaryPeriodStart") },
      { field: "salaryPeriodEnd", title: Translations.get("SALAXY.REPORTS.unionPaymentReport.salaryPeriodEnd") },
      { field: "union", title: Translations.get("SALAXY.REPORTS.unionPaymentReport.union") },
      { field: "membershipPayment", title: Translations.get("SALAXY.REPORTS.unionPaymentReport.membershipPayment") },
      { field: "referenceNumber", title: Translations.get("SALAXY.REPORTS.unionPaymentReport.referenceNumber") },
      { field: "bankAccount", title: Translations.get("SALAXY.REPORTS.unionPaymentReport.bankAccount") },
    ];
    this.unionPaymentExcelRows.push(headers.map(h => h.title));

    let total = 0;
    for (const calc of this.summaryReportData.calculations) {
      const unionPaymentRows = calc.rows.filter(row => row.rowType === CalculationRowType.UnionPayment);

      if (unionPaymentRows.length > 0) {
        unionPaymentRows.forEach(row => {
          total += row.price;

          const workerName = calc.worker?.avatar?.displayName;
          const salaryDate = calc.workflow.salaryDate;
          const periodStart = calc.info.workStartDate;
          const periodEnd = calc.info.workEndDate;
          const unionName = row.data.recipientFullName;
          const payment = row.price;
          const refNumber = row.data.referenceNumber;
          const iban = row.data.iban;

          // Build table data row (for browser display).
          tableData.push({
            worker: workerName,
            salaryDate: Dates.getFormattedDate(salaryDate),
            salaryPeriodStart: Dates.getFormattedDate(periodStart),
            salaryPeriodEnd: Dates.getFormattedDate(periodEnd),
            union: unionName,
            membershipPayment: calc.result.employerCalc.deductionUnionPayment,
            referenceNumber: refNumber,
            bankAccount: iban
          });

          // Build excel row.
          this.unionPaymentExcelRows.push([
            workerName,
            Dates.asJSDate(salaryDate),
            Dates.asJSDate(periodStart),
            Dates.asJSDate(periodEnd),
            unionName,
            payment,
            refNumber,
            iban
          ]);
        });
      }
    }
    // Add total rows to both table and excel.
    tableData.push({
      worker: Translations.get("SALAXY.REPORTS.unionPaymentReport.total"),
      membershipPayment: total
    });
    this.unionPaymentExcelRows.push([Translations.get("SALAXY.REPORTS.unionPaymentReport.total"), "", "", "", "", total]);

    this.reportConfig.unionPaymentTableConfig = UnionPaymentReportConfigGenerator.getConfig(tableData, headers);
    await this.$timeout();
    this.$sxyTable?.refresh();
  }

  /**
   * Creates Cost center report data - for browser and excel export.
   */
  private async createCostCenterReportData() {
    this.costCenterReportCalculations = [];
    // Filter all eligible calcs for this report.
    for (const calc of this.summaryReportData.calculations) {
      const hasDimensions =
        calc.accounting.dimensions.length > 0 ||
        (calc.result.irRows?.some(irRow => irRow.accounting.dimensions.length > 0));

      if (hasDimensions) {
        this.costCenterReportCalculations.push(calc);
      }
    }
    const systemDimensions = this.sessionService.session?.settings?.costAccounting?.dimensions;

    this.reportConfig.costCenterReportTableConfig = CostCenterReportConfigGenerator.getConfig(this.costCenterReportCalculations, systemDimensions);
    await this.$timeout();
    this.$sxyTable?.refresh();
  }

  private createIrReportRows(): void {
    this.resultRows.forEach(resultRow => {
      //Create headers
      const irRows = this.summaryReportData?.calculations?.map(x => x.result.irRows).flat().filter(x => x.irData.code !== 1 && x.irData.code !== 0) ?? [];
      irRows.forEach(irRow => {
        if(!resultRow.irCodeIncomeReportRow.rows.some(x => x.key === `${irRow.data.calculationRowType}-${irRow.irData.code}`)) {
          resultRow.irCodeIncomeReportRow.rows.push({
            key: `${irRow.data.calculationRowType}-${irRow.irData.code}`,
            label: this.getLabel(irRow),
            code: irRow.irData.code,
            isCount: false,
            total: 0
          });
        }
        resultRow.irCodeIncomeReportRow.rows.sort((a, b) => a.code > b.code ? 1 : -1);
      });
      const hourlySalaryRowIndex = resultRow.irCodeIncomeReportRow.rows.findIndex(x => x.key === "HourlySalary-201");
      if(hourlySalaryRowIndex >= 0) {
        resultRow.irCodeIncomeReportRow.rows.splice(hourlySalaryRowIndex + 1, 0, {
          key: "salaryPaymentWorkingHours",
          label: Translations.get("SALAXY.NG1.HolidayReportComponent.salaryPaymentWorkingHours"),
          code: null,
          isCount: true,
          total: 0
        });
      }
      // Insert salaryPaymentMonths after MonthlySalary-201 key
      const monthlySalaryRowIndex = resultRow.irCodeIncomeReportRow.rows.findIndex(x => x.key === "MonthlySalary-201");
      if(monthlySalaryRowIndex >= 0) {
        resultRow.irCodeIncomeReportRow.rows.splice(monthlySalaryRowIndex + 1, 0, {
          key: "salaryPaymentMonths",
          label: Translations.get("SALAXY.NG1.HolidayReportComponent.salaryPaymentMonths"),
          code: null,
          isCount: true,
          total: 0
        });
      }

      // Sum rows
      const item: IrCodeIncomeReportRow = {
        workerAvatar: resultRow.avatar,
        workerId: resultRow.workerId,
        employmentId: resultRow.employmentId,
        rows: [...structuredClone(resultRow.irCodeIncomeReportRow.rows)]
      };
      resultRow.calculations.map(x => x.result.irRows).flat().filter(x => x.irData.code !== 1 && x.irData.code !== 0).forEach(irRow => {
        const row = item.rows.find(x => x.key === `${irRow.data.calculationRowType}-${irRow.irData.code}`);
        row.total += irRow.total;
      });

      const salaryPaymentHoursRow = item.rows.find(x => x.key === "salaryPaymentWorkingHours");
      if(salaryPaymentHoursRow) {
        const hourlySalariesRows = resultRow.calculations.map(x => x.result.irRows.filter(x => x.unit === Unit.Hours && x.irData.code === 201)).flat();
        salaryPaymentHoursRow.total = Arrays.sum(hourlySalariesRows, x => x.count);
      }
      const salaryPaymentMonthsRow = item.rows.find(x => x.key === "salaryPaymentMonths");
      if(salaryPaymentMonthsRow) {
        const monthlySalariesCals = resultRow.calculations.filter(x => x.result.irRows.some(x => /*x.unit === Unit.Period &&*/ x.irData.code === 201));
        salaryPaymentMonthsRow.total = [... new Set(monthlySalariesCals.map(x => Dates.getMonth(x.workflow.salaryDate)))].length;
      }
      if(item.rows.some(x => x.total > 0)) {
        resultRow.irCodeIncomeReportRow = item;
      } else {
        resultRow.irCodeIncomeReportRow = null;
      }
    });
  }

  private createSummaryReportRows() {
    if(!this.summaryReportData || Object.keys(this.summaryReportData).length === 0) {
      return;
    }
    const summaryReportData = Objects.copy(this.summaryReportData);
    this.resultRows.forEach(resultRow => {
      summaryReportData.calculations = resultRow.calculations;
      (SummaryMapper.getSummaryReport(summaryReportData)as any).result.workerResult.forEach(item => {
        this.createSummaryReportRowResult(resultRow, item, "workerResult");
      });
      (SummaryMapper.getSummaryReport(summaryReportData)as any).result.entrepreneurResult.forEach(item => {
        this.createSummaryReportRowResult(resultRow, item, "entrepreneurResult");
      });
      (SummaryMapper.getSummaryReport(summaryReportData)as any).totalResult.forEach(item => {
        this.createSummaryReportRowResult(resultRow, item, "totalResult");
      });
    });
  }

  private createSummaryReportRowResult(resultRow: ResultRow, item: any, resultType: "workerResult" | "entrepreneurResult" | "totalResult"): void {
    if(!resultRow.summaryReportRow.result[resultType]) {
      resultRow.summaryReportRow.result[resultType] = this.getBlankSummaryReportRowResult();
    }
      delete item.worker;
      delete item.workStartDate;
      delete item.workStartEndDate;
      for (const [key, value] of Object.entries(item)) {
        resultRow.summaryReportRow.result[resultType][key] += Numeric.parseNumber(value as string);
        Numeric.round(resultRow.summaryReportRow.result[resultType][key]);
      }
  }

  private getLabel(irRow: IrRow): string {
    let rowType = irRow.data.calculationRowType || CalculationRowType.Unknown;
    rowType = (rowType.substr(0, 1).toLowerCase() + rowType.substr(1)) as CalculationRowType;
    const code = irRow.irData?.code as number;
    const kind = this.getKind(irRow);
    let label = "";
    if (rowType === CalculationRowType.IrIncomeType) {
      label = IncomeTypesLogic.getAll(null).find( (x) => (kind && x.transactionCode === kind) || (code > 0 && x.code === code))?.label + `(${code})`;
    } else if (rowType === CalculationRowType.MealBenefit && code !== 334) {
      // Meal benefit deductions
      label = IncomeTypesLogic.getAll(null).find( (x) => code > 0 && x.code === code)?.label + `(${code})`;
    } else if([
      CalculationRowType.Unknown,
      CalculationRowType.CarBenefit,
      CalculationRowType.BicycleBenefit,
      CalculationRowType.SubsidisedCommute,
      CalculationRowType.TaxAtSource,
      CalculationRowType.HourlySalaryWithWorkingTimeCompensation
    ].includes(rowType))
    {
      label = IncomeTypesLogic.getAll(null).find( (x) => code > 0 && x.code === code)?.label + `(${code})`;
    }
    else if([
      CalculationRowType.DailyAllowance,
      CalculationRowType.EmploymentTermination,
      CalculationRowType.WorkingTimeCompensation,
      CalculationRowType.Remuneration,
      CalculationRowType.OtherCompensation,
      CalculationRowType.NonProfitOrg,
      CalculationRowType.MealBenefit, // main MealBenefit
      CalculationRowType.Training,
      CalculationRowType.PaidSickLeave,
      CalculationRowType.TimeRatePay,
    ].includes(rowType) && kind) {
      label =  EnumerationsLogic.getEnumLabel(`${irRow.data.calculationRowType}Kind`, kind, null, null)  + `(${code})`;
    }
    return label || EnumerationsLogic.getEnumLabel("CalculationRowType", rowType, null, null) + `(${code})`;
  }

  private getKind(irRow: IrRow): string {
    if(irRow.data.kind) {
      return irRow.data.kind;
    }
    // this forward, for backward compatibility
    const rowType = (irRow.data.calculationRowType.substr(0, 1).toLowerCase() + irRow.data.calculationRowType.substr(1)) as CalculationRowType;
    switch(rowType) {
      case CalculationRowType.DailyAllowance: {
        const code = irRow.irData.dailyAllowance[0];
        const codeMapping = {
          [AllowanceCode.InternationalDailyAllowance]: DailyAllowanceKind.InternationalDailyAllowance,
          [AllowanceCode.PartialDailyAllowance]: DailyAllowanceKind.PartialDailyAllowance,
          [AllowanceCode.MealAllowance]: DailyAllowanceKind.MealAllowance,
          [AllowanceCode.FullDailyAllowance]: DailyAllowanceKind.FullDailyAllowance,
        };
        return codeMapping[code] ?? DailyAllowanceKind.Undefined;
      }
      case CalculationRowType.EmploymentTermination: {
        switch(irRow.irData.code) {
          case 208:
            return EmploymentTerminationKind.NoticePeriodCompensation;
          case 224:
            return EmploymentTerminationKind.MonetaryWorkingTimeBankCompensation;
          case 229:
            return EmploymentTerminationKind.TerminationAndLayOffDamages;
          case 231:
            return EmploymentTerminationKind.VoluntaryTerminationCompensation;
          case 338:
            return EmploymentTerminationKind.PensionPaidByEmployer;
        }
        break;
      }
      case CalculationRowType.WorkingTimeCompensation: {
        switch(irRow.irData.code) {
          case 205:
            return WorkingTimeCompensationKind.EmergencyWorkCompensation;
          case 206:
            return WorkingTimeCompensationKind.EveningWorkCompensation;
          case 207:
            return WorkingTimeCompensationKind.EveningShiftAllowance;
          case 211:
            return WorkingTimeCompensationKind.SaturdayPay;
          case 212:
            return WorkingTimeCompensationKind.ExtraWorkPremium;
          case 216:
            return WorkingTimeCompensationKind.OtherCompensation;
          case 217:
            return WorkingTimeCompensationKind.WaitingTimeCompensation;
          case 221:
            return WorkingTimeCompensationKind.SundayWorkCompensation;
          case 230:
            return WorkingTimeCompensationKind.StandByCompensation;
          case 232:
            return WorkingTimeCompensationKind.WeeklyRestCompensation;
          case 235:
            return WorkingTimeCompensationKind.OvertimeCompensation;
          case 236:
            return WorkingTimeCompensationKind.NightWorkAllowance;
          case 237:
            return WorkingTimeCompensationKind.NightShiftCompensation;
        }
        break;
      }
      case CalculationRowType.Remuneration: {
        switch(irRow.irData.code) {
          case 202:
            return RemunerationKind.InitiativeFee;
          case 203:
            return RemunerationKind.BonusPay;
          case 220:
            return RemunerationKind.Commission;
          case 223:
            return RemunerationKind.PerformanceBonus;
          case 226:
            return RemunerationKind.ShareIssueForEmployees;
          case 233:
            return RemunerationKind.ProfitSharingBonus;
        }
        break;
      }
      case CalculationRowType.OtherCompensation: {
        switch(irRow.irData.code) {
          case 210:
            return OtherCompensationKind.MeetingFee;
          case 214:
            return OtherCompensationKind.LectureFee;
          case 215:
            return OtherCompensationKind.PositionOfTrustCompensation;
          case 225:
            return OtherCompensationKind.AccruedTimeOffCompensation;
          case 308:
            return OtherCompensationKind.MembershipOfGoverningBodyCompensation;
          case 310:
            return OtherCompensationKind.MonetaryGiftForEmployees;
          case 313:
            return OtherCompensationKind.UseCompensationAsEarnedIncome;
          case 314:
            return OtherCompensationKind.UseCompensationAsCapitalIncome;
          case 316:
            return OtherCompensationKind.OtherTaxableIncomeAsEarnedIncome;
          case 320:
            return OtherCompensationKind.StockOptionsAndGrants;
          case 326:
            return OtherCompensationKind.EmployeeInventionCompensation;
          case 332:
            return OtherCompensationKind.CapitalIncomePayment;
          case 339:
            return OtherCompensationKind.WorkEffortBasedDividendsAsWage;
          case 340:
            return OtherCompensationKind.WorkEffortBasedDividendsAsNonWage;
          case 343:
            return OtherCompensationKind.EmployeeStockOption;
          case 361:
            return OtherCompensationKind.EmployeeStockOptionWithLowerPrice;
          case 366:
            return OtherCompensationKind.CopyrightRoyaltiesEarnedIncome;
          case 367:
            return OtherCompensationKind.StockGrantPaidInMoney;
          case 368:
            return OtherCompensationKind.EmployeeStockOptionPaidInMoney;
        }
        break;
      }
      case CalculationRowType.NonProfitOrg: {
        switch(irRow.irData.code) {
          case 358:
          case 1358:
            return NonProfitOrgKind.DailyAllowance;
          case 357:
            return NonProfitOrgKind.KilometreAllowance;
        }
        break;
      }
      case CalculationRowType.Training: {
        switch(irRow.irData.code) {
          case 227:
            return TrainingKind.TrainingSalary;
          case 201: {
            if(irRow.unit === Unit.Hours) {
              return TrainingKind.TrainingHourlySalary;
            }
            if(irRow.unit === Unit.Period) {
              return TrainingKind.TrainingMonthlySalary;
            }
          }
        }
        break;
      }
      case CalculationRowType.PaidSickLeave: {
        switch(irRow.irData.code) {
          case 227:
            return PaidSickLeaveKind.PaidSickLeaveSalary;
          case 201: {
            if(irRow.unit === Unit.Hours) {
              return PaidSickLeaveKind.PaidSickLeaveHourlySalary;
            }
            if(irRow.unit === Unit.Period) {
              return PaidSickLeaveKind.PaidSickLeaveMonthlySalary;
            }
          }
        }
        break;
      }
      default:
        return null;
    }
  }

  private getBlankResultRow(): ResultRow {
    return {
      employmentId: null,
      avatar: null,
      workerId: null,
      calculations: [],
      irCodeIncomeReportRow: this.getBlankIrCodeIncomeReportRow(),
      summaryReportRow: this.getBlankSummaryReportRow()
    };
  }

  private getBlankIrCodeIncomeReportRow(): IrCodeIncomeReportRow  {
    return {
      workerAvatar: null,
      workerId: null,
      employmentId: null,
      rows: []
    };
  }

  private getBlankSummaryReportRow(): SummaryReportRow {
    return {
      result: {
        workerResult: null,
        entrepreneurResult: null,
        totalResult: null
      }
    };
  }

  private getBlankSummaryReportRowResult(): SummaryReportRowResult {
    return  {
      avatar: null,
      workerId: null,
      employmentId: null,
      totalGrossSalary: 0,
      totalRowCategory: 0,
      benefits: 0,
      irCode: 0,
      totalTaxable: 0,
      tax: 0,
      pension: 0,
      unemploymentInsurance: 0,
      unionPayment: 0,
      foreclosure: 0,
      otherDeductions: 0,
      totalExpenses: 0,
      totalWorkerPayment: 0,
      totalPayment: 0,
      palkkaus: 0,
      service: 0,
      socialSecurity: 0,
      totalPensions: 0,
      employerPension: 0,
      unemployment: 0,
      employerUnemployment: 0,
    };
  }
}

interface ResultRow {
  employmentId: string;
  avatar: Avatar;
  workerId: string;
  calculations: Calculation[];
  irCodeIncomeReportRow: IrCodeIncomeReportRow;
  summaryReportRow: SummaryReportRow;
}

interface SummaryReportRow {
  result: SummaryReportResult;
}

interface SummaryReportResult {
  workerResult?: SummaryReportRowResult;
  entrepreneurResult?: SummaryReportRowResult;
  totalResult?: SummaryReportRowResult;
}

interface SummaryReportRowResult {
  avatar: Avatar;
  workerId: string;
  employmentId: string;
  totalGrossSalary: number;
  totalRowCategory: number;
  benefits: number;
  irCode: number;
  totalTaxable: number;
  tax: number;
  pension: number;
  unemploymentInsurance: number;
  unionPayment: number;
  foreclosure: number;
  otherDeductions: number;
  totalExpenses: number;
  totalWorkerPayment: number;
  totalPayment: number;
  palkkaus: number;
  service: number;
  socialSecurity: number;
  totalPensions: number;
  employerPension: number;
  unemployment: number;
  employerUnemployment: number;
}

interface IrCodeIncomeReportRow {
  workerAvatar: Avatar;
  workerId: string;
  employmentId: string;
  rows: IrReportRow[];
}

interface IrReportRow {
  code: number;
  key: string;
  total: number;
  label: string;
  isCount: boolean;
}